export class BundlePicker extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'purchaseOption', 'onetimeTotal']
  }

  static get requiredExternalElements () {
    return [
      'submit',
      'summary',
      'totalLabel',
      'summaryTotal',
      'summaryCompare',
      'thumb',
      'priceFrom',
      'pricePerCan',
    ]
  }

  static get requiredAttributes () {
    return ['total-units-text', 'unit-singular', 'unit-plural']
  }

  constructor () {
    super()

    this.products = JSON.parse(this.els.data.element.textContent)
  }

  setupListeners () {
    super.setupListeners()

    this.update = this.update.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onPurchaseOptionChange = this.onPurchaseOptionChange.bind(this)

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.update,
    )

    this.els.purchaseOption.elements.forEach((radio) => {
      this.listeners.add(radio, 'change', this.update)
      this.listeners.add(radio, 'change', this.onPurchaseOptionChange)
    })

    this.submitButtons.forEach((button) => {
      this.listeners.add(button, 'click', this.onSubmit)
    })

    this.updateHeaderPrice()
    this.update()
  }

  /**
   * @returns {void}
   */
  updateHeaderPrice () {
    const { formatMoney } = window.BAO.Utils
    const product = this.products.reduce((lowest, item) => {
      return this.discountedPrice(item.price) < this.discountedPrice(lowest.price)
        ? item
        : lowest
    })
    const discounted = this.discountedPrice(product.price)

    this.setText(this.externalEls, 'priceFrom', formatMoney(discounted))
    this.setText(
      this.externalEls,
      'pricePerCan',
      formatMoney(this.perUnit(discounted, product.priceUnitsPerPack)),
    )
  }

  /**
   *
   * @returns {void}
   */
  update () {
    const { formatMoney } = window.BAO.Utils
    const els = this.allEls
    const isSubscription = this.isSubscription
    const subtotal = this.subtotal
    const discounted = this.subscriptionSubtotal
    const totalCans = this.totalCans

    els.purchaseOption.elements.forEach((radio) => {
      radio
        .closest('[data-bundle-picker-option]')
        .classList.toggle('prd-BundlePicker_Option-selected', radio.checked)
    })

    this.setText(els, 'onetimeTotal', formatMoney(subtotal))
    this.setText(els, 'compareTotal', formatMoney(subtotal))
    this.setText(els, 'total', formatMoney(discounted))
    this.setText(
      els,
      'perCan',
      formatMoney(this.perUnit(discounted, this.totalPriceUnits)),
    )
    this.setText(
      els,
      'onetimePerCan',
      formatMoney(this.perUnit(subtotal, this.totalPriceUnits)),
    )

    this.setText(els, 'totalLabel', this.totalUnitsText)
    this.setText(els, 'summaryCompare', formatMoney(subtotal))
    this.setText(
      els,
      'summaryTotal',
      formatMoney(isSubscription ? discounted : subtotal),
    )

    els.summary.element.hidden = this.totalBoxes === 0

    els.summaryCompare.elements.forEach((el) => {
      el.hidden = !isSubscription
    })

    els.thumb.elements.forEach((thumb) => {
      thumb.classList.toggle(
        'prd-BundlePicker_SummaryThumb-hidden',
        this.quantityFor(thumb.dataset.variantId) === 0,
      )
    })

    this.submitButtons.forEach((button) => {
      if (this.totalBoxes === 0) {
        button.textContent = button.dataset.textEmpty
      } else {
        button.textContent = isSubscription
          ? button.dataset.textSubscription
          : button.dataset.textOnetime
      }

      button.disabled = this.totalBoxes === 0
    })
  }

  /**
   *
   * @param {Object.<string, El>} els
   * @param {string} name
   * @param {string} value
   *
   * @returns {void}
   */
  setText (els, name, value) {
    const el = els[name]

    if (el && el.exists) {
      el.elements.forEach((node) => {
        node.textContent = value
      })
    }
  }

  /**
   * @param {Event} e
   *
   * @returns {Promise<void>}
   */
  async onSubmit (e) {
    e.preventDefault()

    const selected = this.selectedProducts

    if (selected.length === 0) return

    const sellingPlanId = this.isSubscription ? this.sellingPlanId : null

    if (sellingPlanId) {
      const missing = selected.filter(
        ({ product }) => !product.planIds.includes(sellingPlanId),
      )

      if (missing.length > 0) {
        const titles = missing.map(({ product }) => product.title).join(', ')

        this.throwError(
          `${titles} are missing selling plan ${sellingPlanId}. Add them to the Skio selling plan group.`,
        )
      }
    }

    const items = selected.map(({ product, quantity }) => ({
      id: product.variantId,
      quantity,
      ...(sellingPlanId && { selling_plan: sellingPlanId }),
    }))

    this.submitButtons.forEach((button) => {
      button.disabled = true
    })

    try {
      await window.BAO.Cart.addItem(items)
    } finally {
      this.submitButtons.forEach((button) => {
        button.disabled = false
      })
    }
  }

  /**
   * @param {Event} e
   *
   * @returns {void}
   */
  onPurchaseOptionChange (e) {
    const radio = e.target

    if (radio.value !== 'subscription' || !radio.checked) return

    const footer = this.submitButton.closest('.prd-BundlePicker_Footer')

    if (getComputedStyle(footer).position !== 'sticky') return

    const option = radio.closest('[data-bundle-picker-option]')

    window.setTimeout(() => {
      option.style.scrollMarginBlockEnd = `${footer.offsetHeight + 16}px`
      option.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 500)
  }

  /**
   * @returns {Object.<string, El>}
   */
  get allEls () {
    return { ...this.els, ...this.externalEls }
  }

  /**
   * @returns {HTMLButtonElement}
   */
  get submitButton () {
    return this.externalEls.submit.element
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  get submitButtons () {
    return this.externalEls.submit.elements
  }

  /**
   * @returns {{ product: Object, quantity: number }[]}
   */
  get selectedProducts () {
    return this.products
      .map((product) => ({
        product,
        quantity: this.quantityFor(product.variantId),
      }))
      .filter(({ quantity }) => quantity > 0)
  }

  /**
   *
   * @returns {{ product: Object, quantity: number }[]}
   */
  get displayProducts () {
    const selected = this.selectedProducts

    if (selected.length > 0) return selected

    const initial = this.products.find(({ isInitial }) => isInitial)

    return [{ product: initial || this.products[0], quantity: 1 }]
  }

  /**
   * @param {number} variantId
   *
   * @returns {number}
   */
  quantityFor (variantId) {
    const input = this.querySelector(
      `[data-bundle-picker-item][data-variant-id="${variantId}"] [data-quantity-adjuster-el="input"]`,
    )

    return input ? parseInt(input.value, 10) || 0 : 0
  }

  /**
   * @param {number} price
   *
   * @returns {number}
   */
  discountedPrice (price) {
    return price - Math.floor((price * this.discountPercent) / 100)
  }

  /**
   * @param {number} total
   * @param {number} units
   *
   * @returns {number}
   */
  perUnit (total, units) {
    return units === 0 ? 0 : Math.round(total / units)
  }

  /**
   * @param {{ quantity: number }[]} products
   *
   * @returns {number}
   */
  countBoxes (products) {
    return products.reduce((sum, { quantity }) => sum + quantity, 0)
  }

  /**
   * @returns {number}
   */
  get totalBoxes () {
    return this.countBoxes(this.selectedProducts)
  }

  /**
   * @returns {number}
   */
  get totalCans () {
    return this.displayProducts.reduce(
      (sum, { product, quantity }) => sum + product.unitsPerPack * quantity,
      0,
    )
  }

  /**
   * @returns {number}
   */
  get totalPriceUnits () {
    return this.displayProducts.reduce(
      (sum, { product, quantity }) =>
        sum + product.priceUnitsPerPack * quantity,
      0,
    )
  }

  /**
   * @returns {number}
   */
  get subtotal () {
    return this.displayProducts.reduce(
      (sum, { product, quantity }) => sum + product.price * quantity,
      0,
    )
  }

  /**
   * @returns {number}
   */
  get subscriptionSubtotal () {
    return this.displayProducts.reduce(
      (sum, { product, quantity }) =>
        sum + this.discountedPrice(product.price) * quantity,
      0,
    )
  }

  /**
   * @returns {number}
   */
  get discountPercent () {
    return Number(this.getAttribute('discount-percent')) || 0
  }

  /**
   * @returns {boolean}
   */
  get isSubscription () {
    return this.els.purchaseOption.elements.some(
      (radio) => radio.checked && radio.value === 'subscription',
    )
  }

  /**
   * @returns {number|null}
   */
  get sellingPlanId () {
    const frequency = this.els.frequency

    return frequency && frequency.exists
      ? Number(frequency.element.value)
      : null
  }

  /**
   * @returns {string}
   */
  get totalUnitsText () {
    const unit = this.totalCans === 1
      ? this.getAttribute('unit-singular')
      : this.getAttribute('unit-plural')

    return this.getAttribute('total-units-text')
      .replace('[count]', this.totalCans)
      .replace('[unit]', unit)
  }
}

if (!customElements.get('bundle-picker')) {
  customElements.define('bundle-picker', BundlePicker)
}
